import "./RadioButtonControlGroup.scss";

const DEFAULT_TONE = "default";

/**
 * Segmented radio-button-style control group with an animated background pill.
 *
 * Supports two ARIA roles via `role`:
 * - `"radiogroup"` (default): options get `role="radio"` + `aria-checked`.
 * - `"tablist"`: options get `role="tab"` + `aria-selected`, with optional
 *   `controls` (→ `aria-controls`) and `id` for linking to tab panels.
 *
 * Each option may set a `tone` color scheme (`"default"`, `"green"`, `"red"`, `"pending"`).
 * The animated pill picks up the tone of the currently selected option and
 * selected segments tint their text to match (green/red/pending).
 *
 * @param {object} props
 * @param {"tablist" | "radiogroup"} [props.role="radiogroup"] Group-level ARIA role.
 * @param {string} [props.ariaLabel]
 * @param {string} [props.ariaLabelledBy]
 * @param {string | number | null} props.value Currently selected option value.
 * @param {(value: string | number | null) => void} props.onChange
 * @param {Array<{
 *   value: string | number | null,
 *   label: React.ReactNode,
 *   id?: string,
 *   controls?: string,
 *   tone?: "default" | "green" | "red" | "pending",
 * }>} props.options
 * @param {string} [props.minCh] CSS length for each segment's min-width (e.g. `"11ch"`).
 *   Defaults to the longest of the component's built-in sizing (`11ch`).
 * @param {string} [props.className] Extra class merged onto the root.
 * @returns {import('react').JSX.Element}
 */
const RadioButtonControlGroup = ({
    role = "radiogroup",
    ariaLabel,
    ariaLabelledBy,
    value,
    onChange,
    options,
    minCh,
    className,
}) => {
    const isTablist = role === "tablist";
    const segmentRole = isTablist ? "tab" : "radio";

    const activeIndex = Math.max(
        0,
        options.findIndex((opt) => opt.value === value),
    );
    const activeOption = options[activeIndex];
    const pillTone = activeOption?.tone ?? DEFAULT_TONE;

    const rootStyle = {
        "--segment-count": options.length,
        "--active-index": activeIndex,
    };
    if (minCh) {
        rootStyle["--segment-min-ch"] = minCh;
    }

    const rootClassName = ["radio-button-control-group", className]
        .filter(Boolean)
        .join(" ");

    return (
        <div
            className={rootClassName}
            style={rootStyle}
            role={role}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
        >
            <span
                className={`radio-button-control-group__pill radio-button-control-group__pill--${pillTone}`}
                aria-hidden
            />
            {options.map((option) => {
                const tone = option.tone ?? DEFAULT_TONE;
                const isSelected = option.value === value;
                const segmentClassName = [
                    "radio-button-control-group__segment",
                    `radio-button-control-group__segment--${tone}`,
                    isSelected ? "is-selected" : null,
                ]
                    .filter(Boolean)
                    .join(" ");

                const ariaStateProps = isTablist
                    ? {
                          "aria-selected": isSelected,
                          "aria-controls": option.controls,
                      }
                    : { "aria-checked": isSelected };

                return (
                    <button
                        key={String(option.value)}
                        type="button"
                        role={segmentRole}
                        id={option.id}
                        className={segmentClassName}
                        onClick={() => onChange(option.value)}
                        {...ariaStateProps}
                    >
                        {option.label}
                    </button>
                );
            })}
        </div>
    );
};

export default RadioButtonControlGroup;
